/**
 * Life-event self-service flow for employees.
 *
 * Route: /benefits/life-event
 *
 * Employees pick what happened from a list of qualifying events; we
 * show the IRS-style 30-day deadline; they upload supporting
 * documentation and submit. The backend opens a "special" enrollment
 * period scoped to their employee_id when an admin reviews the event.
 *
 * Pre-existing infrastructure:
 *   - Backend ``POST /benefits/me/life-events`` (already wired)
 *   - Admin review at ``LifeEventsPage`` spawns a 30-day window
 *
 * The page is intentionally simpler than the enrollment wizard — life
 * events are time-pressured, so the UX prioritizes "get it submitted"
 * over "explore options".
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Baby,
  Calendar,
  Check,
  CheckCircle2,
  FileText,
  Heart,
  HeartCrack,
  Home,
  ShieldOff,
  Upload,
  UserMinus,
  UserX,
} from "lucide-react";

import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { reportLifeEvent, type LifeEventType } from "@/api/benefits";

type Step = "event" | "details" | "done";

interface EventOption {
  value: LifeEventType | "moved" | "other";
  label: string;
  description: string;
  icon: typeof Heart;
  /**
   * If the backend doesn't recognize this option (e.g. "moved" /
   * "other"), we route it to the closest supported type and surface
   * the original choice in the notes. Keeps the UI faithful to the
   * spec without requiring a schema migration.
   */
  backendType: LifeEventType;
  /**
   * Free-text addition automatically dropped into the notes field
   * when this option doesn't have a 1:1 backend type.
   */
  notesPrefix?: string;
}

const EVENT_OPTIONS: EventOption[] = [
  {
    value: "marriage",
    label: "I got married",
    description: "Adding a spouse to your coverage.",
    icon: Heart,
    backendType: "marriage",
  },
  {
    value: "birth",
    label: "I had a baby / adopted a child",
    description: "Adding a newborn or new child to your coverage.",
    icon: Baby,
    backendType: "birth",
  },
  {
    value: "divorce",
    label: "I got divorced / legally separated",
    description: "Removing a spouse from your coverage.",
    icon: HeartCrack,
    backendType: "divorce",
  },
  {
    value: "loss_of_coverage",
    label: "My spouse lost their coverage",
    description: "Adding family members previously covered elsewhere.",
    icon: ShieldOff,
    backendType: "loss_of_coverage",
  },
  {
    value: "loss_of_coverage",
    label: "I lost other coverage",
    description: "Enrolling because you lost coverage elsewhere.",
    icon: UserX,
    backendType: "loss_of_coverage",
    notesPrefix: "Employee lost other coverage.",
  },
  {
    value: "dependent_aging_out",
    label: "I turned 26 (aging off parent's plan)",
    description: "Enrolling on your own coverage for the first time.",
    icon: UserMinus,
    backendType: "dependent_aging_out",
  },
  {
    value: "moved",
    label: "I moved to a new state",
    description: "Changing plans for your new location.",
    icon: Home,
    backendType: "loss_of_coverage",
    notesPrefix: "Employee moved to a new state.",
  },
  {
    value: "other",
    label: "Other qualifying event",
    description: "Death of a dependent, USERRA, court order, etc.",
    icon: FileText,
    backendType: "death",
    notesPrefix: "Other qualifying event (see notes).",
  },
];

export function BenefitsLifeEventPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [step, setStep] = useState<Step>("event");
  const [selected, setSelected] = useState<EventOption | null>(null);
  const [eventDate, setEventDate] = useState("");
  const [notes, setNotes] = useState("");
  const [docName, setDocName] = useState<string | null>(null);
  // For demo simplicity, we capture the document's filename but don't
  // wire R2 here — the backend accepts a ``documentation_url`` that's
  // populated after a future upload pass. The toast tells the user
  // they can add the doc in the existing portal.
  const [submitting, setSubmitting] = useState(false);
  const [submittedLifeEventId, setSubmittedLifeEventId] = useState<
    string | null
  >(null);

  // Compute the 30-day deadline relative to the event date so the
  // banner can render "You have N days from <eventDate>".
  const deadline = useMemo(() => {
    if (!eventDate) return null;
    const start = new Date(eventDate);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 30);
    return end;
  }, [eventDate]);

  const daysLeft = useMemo(() => {
    if (!deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ms = deadline.getTime() - today.getTime();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [deadline]);

  const handleSelect = (opt: EventOption) => {
    setSelected(opt);
    if (opt.notesPrefix) setNotes(opt.notesPrefix + " ");
  };

  const handleSubmit = async () => {
    if (!selected || !eventDate) return;
    setSubmitting(true);
    try {
      const res = await reportLifeEvent({
        event_type: selected.backendType,
        event_date: eventDate,
        notes: notes.trim() || undefined,
        documentation_name: docName ?? undefined,
      });
      setSubmittedLifeEventId(res.id);
      setStep("done");
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      const detail = e?.response?.data?.detail;
      addToast(
        "error",
        typeof detail === "string"
          ? detail
          : "Couldn't submit your life event. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-bg">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {step !== "done" && (
          <button
            onClick={() =>
              step === "event" ? navigate("/benefits") : setStep("event")
            }
            className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-primary mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            {step === "event" ? "Back to benefits" : "Choose a different event"}
          </button>
        )}

        {step === "event" && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs uppercase tracking-wide text-accent font-semibold mb-2">
              Report a life event
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
              What happened?
            </h1>
            <p className="text-base text-text-muted mb-8">
              We&apos;ll open a 30-day window for you to update your
              benefits. Pick the one that matches.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {EVENT_OPTIONS.map((opt, i) => {
                const Icon = opt.icon;
                const isSelected =
                  selected?.label === opt.label && selected.value === opt.value;
                return (
                  <button
                    key={i}
                    onClick={() => handleSelect(opt)}
                    className={`relative text-left rounded-2xl border-2 px-5 py-4 transition-all ${
                      isSelected
                        ? "border-accent bg-accent/5 shadow-md"
                        : "border-border bg-surface hover:border-accent/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`rounded-lg p-2 ${
                          isSelected
                            ? "bg-accent text-white"
                            : "bg-accent/10 text-accent"
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-text-primary">
                          {opt.label}
                        </p>
                        <p className="text-sm text-text-muted mt-0.5">
                          {opt.description}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="h-5 w-5 text-accent shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                onClick={() => setStep("details")}
                disabled={!selected}
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {step === "details" && selected && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs uppercase tracking-wide text-accent font-semibold mb-2">
              {selected.label}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
              When did it happen?
            </h1>

            <div className="space-y-4">
              <div>
                <Input
                  label="Event date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  required
                />
                {deadline && daysLeft !== null && (
                  <div
                    className={`mt-3 rounded-xl px-4 py-3 flex items-start gap-3 ${
                      daysLeft <= 0
                        ? "bg-danger/10 text-danger"
                        : daysLeft <= 7
                        ? "bg-warning/10 text-warning"
                        : "bg-accent/10 text-accent"
                    }`}
                  >
                    {daysLeft <= 0 ? (
                      <AlertTriangle className="h-5 w-5 mt-0.5" />
                    ) : (
                      <Calendar className="h-5 w-5 mt-0.5" />
                    )}
                    <div className="text-sm">
                      <p className="font-semibold">
                        {daysLeft <= 0
                          ? "This event is more than 30 days old."
                          : `You have ${daysLeft} ${
                              daysLeft === 1 ? "day" : "days"
                            } left to make changes.`}
                      </p>
                      <p className="text-xs opacity-80 mt-0.5">
                        {daysLeft <= 0
                          ? "We'll still submit it for HR review, but the window may have closed."
                          : `Deadline: ${deadline.toLocaleDateString(
                              undefined,
                              {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Notes (what you&apos;d like HR to know)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
                  placeholder="Anything that helps your HR team approve faster."
                />
              </div>

              {/* Documentation upload — captures filename only for now.
                  R2 upload will be wired in a follow-up. */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Supporting documentation
                </label>
                <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-border bg-surface px-4 py-4 cursor-pointer hover:border-accent/40">
                  <Upload className="h-5 w-5 text-text-muted" />
                  <span className="text-sm text-text-muted flex-1">
                    {docName
                      ? docName
                      : "Marriage certificate, birth certificate, COBRA letter, etc."}
                  </span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setDocName(f.name);
                    }}
                  />
                </label>
                <p className="text-xs text-text-muted mt-2">
                  You can also upload later from the Benefits page if you
                  don&apos;t have documents handy.
                </p>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setStep("event")}>
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={submitting}
                disabled={!eventDate}
              >
                Submit life event
              </Button>
            </div>
          </motion.div>
        )}

        {step === "done" && selected && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-success/10 mb-6">
              <CheckCircle2 className="h-12 w-12 text-success" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
              Submitted!
            </h1>
            <p className="text-lg text-text-muted mb-6">
              We&apos;ve notified your HR team. Once approved, you&apos;ll
              get a 30-day window to update your coverage.
            </p>
            <p className="text-sm text-text-muted mb-8 max-w-md mx-auto">
              You can start picking new plans right away — your changes
              will go live once HR confirms the qualifying event.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="secondary" onClick={() => navigate("/benefits")}>
                Back to benefits
              </Button>
              <Button onClick={() => navigate("/benefits/enroll")}>
                Start enrollment changes
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            {submittedLifeEventId && (
              <p className="text-xs text-text-muted mt-6">
                Reference: {submittedLifeEventId.slice(0, 8)}
              </p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
